// src/domains/core/context-intelligence/services/evaluationService.ts

import type { Insight, InsightEvaluation } from "../types/insights.types";
import type { RelationshipEvaluationResult } from "../types/relationships.types";

import { PriorityEngine } from "../engine/priorityEngine";
import { insightService } from "./insightService";

/**
 * WHAT:
 *   Evaluation Service - Evaluates and re-prioritizes existing insights.
 *
 * WHY:
 *   Provides centralized evaluation logic for insights with relationship validation.
 *
 * BEHAVIOR:
 *   - Recalculates relevance and confidence
 *   - Validates insight against relationships
 *   - Creates evaluation records
 *   - Archives outdated insights
 *
 * RISKS:
 *   - NOTE: Evaluation may fail silently in bulk operations
 *   - NOTE: Archive threshold (30 days, relevance < 0.3) is hardcoded
 */
export const evaluationService = {
  /**
   * WHAT:
   *   Evaluate an insight and update its priority/relevance.
   *
   * WHY:
   *   Re-evaluates insights based on current relationship state and recalculates metrics.
   *
   * @param {Insight} insight - Insight to evaluate
   *
   * @param {RelationshipEvaluationResult[]} [relationshipResults] - Optional relationship evaluation results
   *
   * @returns {Promise<InsightEvaluation>} Evaluation result
   *
   */
  async evaluateInsight(
    insight: Insight,
    relationshipResults?: RelationshipEvaluationResult[],
  ): Promise<InsightEvaluation> {
    // Recalculate relevance and confidence
    const evaluation = PriorityEngine.evaluateInsight(insight, relationshipResults);

    // Check if insight is still valid
    const stillValid = isInsightStillValid(insight, relationshipResults);

    // Create evaluation record
    const evaluationRecord: Omit<InsightEvaluation, "evaluatedAt" | "id"> = {
      confidence: evaluation.confidence,
      evaluator: "system",
      evidence: {
        indicatorValues: {}, // To be populated with actual values
        relationshipSatisfied: relationshipResults?.some((r) => r.satisfied) ?? false,
      },
      insightId: insight.id,
      insightVersion: insight.version,
      relevance: evaluation.relevance,
      stillValid,
    };

    return insightService.createEvaluation(evaluationRecord);
  },

  /**
   * WHAT:
   *   Archive outdated insights.
   *
   * WHY:
   *   Removes old, low-relevance insights from active view.
   *
   * @returns {Promise<number>} Number of archived insights
   *
   */
  async archiveOutdatedInsights(): Promise<number> {
    const insights = await insightService.getActiveInsights();
    let archivedCount = 0;

    for (const insight of insights) {
      // Check if insight is outdated
      const age = Date.now() - insight.generatedAt;
      const ageInDays = age / (1000 * 60 * 60 * 24);

      // Archive insights older than 30 days with low relevance
      if (ageInDays > 30 && insight.relevance < 0.3) {
        await insightService.archiveInsight(insight.id, insight.version);
        archivedCount++;
      }
    }

    return archivedCount;
  },

  /**
   * WHAT:
   *   Re-evaluate all active insights.
   *
   * WHY:
   *   Bulk re-evaluation of all active insights with updated relationship data.
   *
   * @param {(insight: Insight) => Promise<RelationshipEvaluationResult[]>} getRelationshipResults - Function to get relationship results for an insight
   *
   * @returns {Promise<InsightEvaluation[]>} Array of evaluation results
   *
   */
  async reevaluateAllInsights(
    getRelationshipResults: (insight: Insight) => Promise<RelationshipEvaluationResult[]>,
  ): Promise<InsightEvaluation[]> {
    const insights = await insightService.getActiveInsights();
    const evaluations: InsightEvaluation[] = [];

    for (const insight of insights) {
      try {
        const relationshipResults = await getRelationshipResults(insight);
        const evaluation = await evaluationService.evaluateInsight(insight, relationshipResults);
        evaluations.push(evaluation);

        // Update insight if priority changed significantly
        const newPriority = PriorityEngine.assignPriority(
          evaluation.relevance,
          evaluation.confidence,
        );

        if (newPriority !== insight.priority) {
          // Create new version with updated priority
          await insightService.createInsight({
            ...insight,
            confidence: evaluation.confidence,
            priority: newPriority,
            relevance: evaluation.relevance,
          });
        }
      } catch {
        // NOTE: Failed evaluations are skipped to avoid blocking bulk reevaluation
      }
    }

    return evaluations;
  },
};

/**
 * WHAT:
 *   Check if insight is still valid.
 *
 * WHY:
 *   Validates insight against expiration, relationships, and confidence thresholds.
 *
 * @param {Insight} insight - Insight to validate
 *
 * @param {RelationshipEvaluationResult[]} [relationshipResults] - Optional relationship evaluation results
 *
 * @returns {boolean} True if insight is still valid
 *
 */
function isInsightStillValid(
  insight: Insight,
  relationshipResults?: RelationshipEvaluationResult[],
): boolean {
  // Check if valid_until has passed
  if (insight.validUntil && Date.now() > insight.validUntil) {
    return false;
  }

  // Check if relationships are still satisfied
  if (relationshipResults && relationshipResults.length > 0) {
    const allSatisfied = relationshipResults.every((r) => r.satisfied);
    if (!allSatisfied) {
      return false;
    }
  }

  // Check confidence threshold
  if (insight.confidence < 0.3) {
    return false;
  }

  return true;
}
